import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { RootState } from "app/store";
import { getData } from "features/dinamic";

interface AlumnoDashboardState {
  status: string;
  error: string;
  kpis: {
    totalPendientes: number;
    totalEntregadas: number;
    totalCalificadas: number;
  } | null;
  actividadesProximas: any[];
  actividadesVencidas: any[];
  entregasRecientes: any[];
  actividades: any[];
  entregas: any[];
  calificaciones: any[];
  grupoInfo: {
    idGrupo: number | null;
    nombreGrupo: string | null;
  } | null;
  actividadesTotal: number;
  entregasTotal: number;
  actividadesPage: number;
  entregasPage: number;
}

const initialState: AlumnoDashboardState = {
  status: 'idle',
  error: '',
  kpis: null,
  actividadesProximas: [],
  actividadesVencidas: [],
  entregasRecientes: [],
  actividades: [],
  entregas: [],
  calificaciones: [],
  grupoInfo: null,
  actividadesTotal: 0,
  entregasTotal: 0,
  actividadesPage: 1,
  entregasPage: 1,
}

export const fetchDashboardAlumno = createAsyncThunk('alumno/fetchDashboard', async (_: any, { dispatch, getState, rejectWithValue }) => {
  const idsp = 201;
  const { result, message, data } = await getData({ idsp }, getState, dispatch);
  if (result) {
    return { data };
  }
  return rejectWithValue(message);
});

export const fetchActividadesGlobalAlumno = createAsyncThunk('alumno/fetchActividadesGlobal', async ({ estado, idCiclo, idModulo, idSesion, idGrupo, page = 1, pageSize = 5, append = false }: any, { dispatch, getState, rejectWithValue }) => {
  const idsp = 208;
  const { result, message, data } = await getData({ idsp, estado, idCiclo, idModulo, idSesion, idGrupo, page, pageSize }, getState, dispatch);
  if (result) {
    return { data, append };
  }
  return rejectWithValue(message);
});

export const fetchEntregasGlobalAlumno = createAsyncThunk('alumno/fetchEntregasGlobal', async ({ estado, idCiclo, idModulo, idSesion, idGrupo, page = 1, pageSize = 5, append = false }: any, { dispatch, getState, rejectWithValue }) => {
  const idsp = 209; 
  const { result, message, data } = await getData({ idsp, estado, idCiclo, idModulo, idSesion, idGrupo, page, pageSize }, getState, dispatch);
  if (result) {
    return { data, append };
  }
  return rejectWithValue(message);
});


const alumnoDashboardSlice = createSlice({
  name: 'alumnoDashboard',
  initialState,
  reducers: {
    clearAlumnoDashboard: () => initialState,
    invalidateDashboardCache: (state) => {
      state.status = 'idle';
    },
  },
  extraReducers(builder) {
    builder
      .addCase(fetchDashboardAlumno.pending, (state) => { state.status = 'loading'; })
      .addCase(fetchDashboardAlumno.fulfilled, (state, action) => {
        const data = action.payload?.data || {};
        state.status = 'completed';
        state.kpis = {
          totalPendientes: data.totalPendientes || 0,
          totalEntregadas: data.totalEntregadas || 0,
          totalCalificadas: data.totalCalificadas || 0,
        };
        state.actividadesProximas = data.actividadesProximas || [];
        state.actividadesVencidas = data.actividadesVencidas || [];
        state.entregasRecientes = data.entregasRecientes || [];
        if (data.idGrupo || data.nombreGrupo) {
          state.grupoInfo = {
            idGrupo: data.idGrupo || null,
            nombreGrupo: data.nombreGrupo || null,
          };
        }
      })
      .addCase(fetchDashboardAlumno.rejected, (state, action) => { state.status = 'failed'; state.error = action.payload as string; })
      .addCase(fetchActividadesGlobalAlumno.fulfilled, (state, action) => {
        const data = action.payload?.data;
        const append = action.payload?.append;
        let items = [];
        if (Array.isArray(data)) {
          items = data;
        } else if (data?.items) {
          items = data.items;
          state.actividadesTotal = data.total || 0;
        }

        if (append) {
          state.actividades = [...state.actividades, ...items];
          state.actividadesPage += 1;
        } else {
          state.actividades = items;
          state.actividadesPage = 1;
          state.actividadesTotal = data?.total || items.length;
        }
      })
      .addCase(fetchEntregasGlobalAlumno.fulfilled, (state, action) => {
        const data = action.payload?.data;
        const append = action.payload?.append;
        let items = [];
        if (Array.isArray(data)) {
          items = data;
        } else if (data?.items) {
          items = data.items;
          state.entregasTotal = data.total || 0;
        }

        if (append) {
          state.entregas = [...state.entregas, ...items];
          state.entregasPage += 1;
        } else {
          state.entregas = items;
          state.entregasPage = 1;
          state.entregasTotal = data?.total || items.length;
        }
      })
  }
});

export const selectAlumnoDashboardKpis = (state: RootState) => state.alumnoDashboard.kpis;
export const selectAlumnoDashboardActividadesProximas = (state: RootState) => state.alumnoDashboard.actividadesProximas;
export const selectAlumnoDashboardActividadesVencidas = (state: RootState) => state.alumnoDashboard.actividadesVencidas;
export const selectAlumnoDashboardEntregasRecientes = (state: RootState) => state.alumnoDashboard.entregasRecientes;
export const selectAlumnoDashboardActividades = (state: RootState) => state.alumnoDashboard.actividades;
export const selectAlumnoDashboardEntregas = (state: RootState) => state.alumnoDashboard.entregas;
export const selectAlumnoDashboardGrupoInfo = (state: RootState) => state.alumnoDashboard.grupoInfo;
export const selectAlumnoDashboardActividadesTotal = (state: RootState) => state.alumnoDashboard.actividadesTotal;
export const selectAlumnoDashboardEntregasTotal = (state: RootState) => state.alumnoDashboard.entregasTotal;
export const selectAlumnoDashboardActividadesPage = (state: RootState) => state.alumnoDashboard.actividadesPage;
export const selectAlumnoDashboardEntregasPage = (state: RootState) => state.alumnoDashboard.entregasPage;

export const { clearAlumnoDashboard, invalidateDashboardCache } = alumnoDashboardSlice.actions;

export default alumnoDashboardSlice.reducer;